import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { showToast } from "../../components/toast";
import CompanyHeader from "./CompanyHeader";
import { InputBox } from "../../components/Input";
import { PermissionWrapper } from "../../helpers/permission.js";

const CarparvaOutputLoginForm = () => {
  const [formData, setFormData] = useState({
    username: "100360B",
    password: "",
    role: "RA",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);

  const token = localStorage.getItem("authToken");

  useEffect(() => {
    fetchCarparvaOutputDetails();
  }, []);

  const fetchCarparvaOutputDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}settings/company/carparva-output-auth`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        setFormData({
          username: data.username || "100360B",
          password: data.password || "",
          role: data.role || "RA",
        });
      }
    } catch (err) {
      console.error("Error fetching Carparva Output details:", err);
      showToast.error("Failed to fetch Carparva Output settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();

    if (!formData.username?.trim()) {
      showToast.error("Please enter Carparva Output Username.");
      return;
    }
    if (!formData.password?.trim()) {
      showToast.error("Please enter Carparva Output Password.");
      return;
    }
    if (!formData.role?.trim()) {
      showToast.error("Please enter Carparva Output Role.");
      return;
    }

    try {
      setLoggingIn(true);
      const response = await axios.post(
        `${API_URL}settings/company/carparva-output-login`,
        {
          username: formData.username.trim(),
          password: formData.password.trim(),
          role: formData.role.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        showToast.success("Carparva Output login successful!");
      } else {
        showToast.error(
          response.data?.message || "Carparva Output login failed."
        );
      }
    } catch (err) {
      console.error("Error logging in to Carparva Output:", err);
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to authenticate with Carparva Output.";
      showToast.error(errorMsg);
    } finally {
      setLoggingIn(false);
    }
  };

  if (loading) {
    return (
      <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
        <div id="content">
          <CompanyHeader />
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        <CompanyHeader />

        <div className="panel panel-default with-rounded-corner with-shadow mt-3">
          <form onSubmit={handleLogin}>
            <div className="panel-heading border-0">
              <span className="panel-title">Carparva Output Login</span>
            </div>

            <div className="panel-body mt-2">
              <div className="row">
                {/* Username */}
                <div className="col-md-4 mb-2">
                  <InputBox
                    label="Username"
                    fieldname="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="Enter Username"
                    required
                  />
                </div>

                {/* Password */}
                <div className="col-md-4 mb-2">
                  <div className="form-group">
                    <label className="control-label">Password</label>
                    <div className="input-group" style={{ display: "flex" }}>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        id="password"
                        className="form-control"
                        placeholder="Enter Password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                      />
                      <button
                        type="button"
                        className="btn btn-default"
                        onClick={() => setShowPassword((prev) => !prev)}
                        title={showPassword ? "Hide password" : "Show password"}
                        style={{
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          borderLeft: "none",
                        }}
                      >
                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Role */}
                <div className="col-md-4 mb-2">
                  <InputBox
                    label="Role"
                    fieldname="role"
                    value={formData.role}
                    onChange={handleChange}
                    placeholder="Enter Role (e.g. RA)"
                    required
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="clearfix"></div>
              <PermissionWrapper module="settings_company" action="edit">
                <div className="row">
                  <div className="col-lg-12">
                    <div style={{ float: "left", marginTop: "15px" }}>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loggingIn}
                      >
                        {loggingIn ? "Logging in..." : "Login"}
                      </button>
                    </div>
                    <div style={{ float: "left", marginTop: "15px", paddingLeft: "10px" }}>
                      <Link to="/Settings/Company" className="btn btn-danger">
                        Cancel
                      </Link>
                    </div>
                  </div>
                </div>
              </PermissionWrapper>
            </div>
          </form>
        </div>

        {/* Scroll to Top Button */}
        <Link to="#" data-click="scroll-top" className="btn-scroll-top fade">
          <i className="ti-arrow-up"></i>
        </Link>
      </div>
    </div>
  );
};

export default CarparvaOutputLoginForm;
